import React, { useState } from "react";
import { Link } from "react-router-dom";
import { X, Download, ExternalLink, Loader2, Copy, Check, Send, FolderInput } from "lucide-react";
import toast from "react-hot-toast";

import { downloadCmaPdf, saveCmaToProperty, sendCmaReport } from "../api/prospectingReportsApi";

const TEAL = "#55c5d0";

/**
 * "Report ready" — the end of the wizard.
 *
 * Mirrors Ignite's final dialog (Download PDF / View digital report / search for
 * another property) and adds the step Ignite handles elsewhere: emailing it to
 * the vendor. Download PDF greys out on failure rather than blocking, because
 * the digital link is the primary deliverable and PDF rendering depends on a
 * server-side engine that can be unavailable.
 */
const ReportReadyModal = ({ report, onClose, onDone }) => {
  const [downloading, setDownloading] = useState(false);
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [filing, setFiling] = useState(false);
  const [filed, setFiled] = useState(null);
  const [recipient, setRecipient] = useState({
    name: report?.recipient_name || "",
    email: report?.recipient_email || "",
    message: "",
  });

  const shareUrl = `${window.location.origin}/cma/${report?.share_token}`;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const { data } = await downloadCmaPdf(report.id);
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${report.report_type_label || "CMA Report"}, ${report.full_address}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Could not build the PDF. The digital report link still works.");
    } finally {
      setDownloading(false);
    }
  };

  // Christian (Monday 13066303151): the document should go straight into the
  // Property Profile's Documents tab.
  const handleFile = async () => {
    setFiling(true);
    try {
      const { data } = await saveCmaToProperty(report.id);
      setFiled(data);
      toast.success(data.created ? "Saved in Property Documents." : "Already saved in Property Documents.");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not save the document.");
    } finally {
      setFiling(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Could not copy the link.");
    }
  };

  const handleSend = async () => {
    if (!recipient.email.trim()) {
      toast.error("Enter the vendor's email address.");
      return;
    }
    setSending(true);
    try {
      await sendCmaReport(report.id, {
        recipient_email: recipient.email.trim(),
        recipient_name: recipient.name.trim(),
        message: recipient.message.trim(),
      });
      toast.success("Report sent.");
      onDone?.();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not send the report.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-end px-4 pt-4">
          <button type="button" onClick={onClose} className="p-1 rounded-lg hover:bg-gray-50 text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 pb-6 text-center">
          <div
            className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
            style={{ backgroundColor: "var(--c-panel-brand)" }}
          >
            <Check className="w-8 h-8" style={{ color: TEAL }} />
          </div>
          <h2 className="text-xl font-bold text-gray-900">
            {report?.report_type === "price_statement" ? "Statement published" : "Report ready"}
          </h2>
          <p className="text-sm text-gray-500 mt-1 mb-5">
            {report?.report_type === "price_statement"
              ? "The Property Price Statement is now public at this link for at least 18 months."
              : "More knowledge at their fingertips. Share a link to the digital property report."}
          </p>

          <div className="flex items-center gap-2 mb-4">
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Download PDF
            </button>
            <a
              href={shareUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-white text-sm font-semibold"
              style={{ backgroundColor: TEAL }}
            >
              <ExternalLink className="w-4 h-4" />
              View digital report
            </a>
          </div>

          {filed ? (
            <Link
              to={`/property/${filed.property_id}?tab=documents`}
              title={`Open ${filed.property_address} > Documents`}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-green-200 bg-green-50 text-sm font-semibold text-green-700 hover:bg-green-100 mb-4"
            >
              <Check className="w-4 h-4" />
              Saved in Property Documents
              <ExternalLink className="w-3.5 h-3.5" />
            </Link>
          ) : (
            <button
              type="button"
              onClick={handleFile}
              disabled={filing}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 mb-4"
            >
              {filing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderInput className="w-4 h-4" />}
              Save Document in Property Documents
            </button>
          )}

          <button
            type="button"
            onClick={handleCopy}
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-gray-50 text-xs text-gray-600 hover:bg-gray-100 mb-5 truncate"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
            <span className="truncate">{copied ? "Link copied" : shareUrl}</span>
          </button>

          <div className="pt-5 border-t border-gray-100 text-left">
            <p className="text-sm font-bold text-gray-900 mb-2">Email it to the vendor</p>
            <div className="space-y-2">
              <input
                type="text"
                value={recipient.name}
                onChange={(e) => setRecipient({ ...recipient, name: e.target.value })}
                placeholder="Vendor name"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
              />
              <input
                type="email"
                value={recipient.email}
                onChange={(e) => setRecipient({ ...recipient, email: e.target.value })}
                placeholder="vendor@example.com"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
              />
              <textarea
                rows={2}
                value={recipient.message}
                onChange={(e) => setRecipient({ ...recipient, message: e.target.value })}
                placeholder="Optional note to include in the email"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
              />
              <button
                type="button"
                onClick={handleSend}
                disabled={sending}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
                style={{ backgroundColor: TEAL }}
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send report
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportReadyModal;
